import { usePermissions } from './usePermissions';
import { isOrgModule, useOrgModules } from './useOrgModules';
import { LANDING_PAGE_META } from '../types/profiles';
import type { ControlledResource, LandingPage } from '../types/profiles';
import type { OrgLanding } from '../types';

/**
 * Ordered fallback candidates when a profile's chosen landing page isn't
 * accessible (or none is set). Walked top-to-bottom; the first page the user
 * can read wins. Loop-safe: guards that redirect here always land on a page the
 * user is actually allowed to open.
 */
const FALLBACK_CANDIDATES: { resource: ControlledResource; path: string }[] = [
  { resource: 'genie-artifact', path: '/welcome' },
  { resource: 'domains', path: '/dashboard' },
  { resource: 'domains', path: '/domains' },
  { resource: 'usecases', path: '/cas-usage/list' },
  { resource: 'conversations', path: '/conversations' },
  { resource: 'chatbots', path: '/chatbots' },
  { resource: 'knowledge-base', path: '/knowledge-base' },
  { resource: 'cadrages', path: '/cadrages' },
];

// Ultimate fallback: the profile page is never permission-gated.
const SAFE_FALLBACK = '/profil';

/** Org landing (ui_settings.landing) → the landing page it stands for. */
export const ORG_LANDING_PAGE: Record<OrgLanding, LandingPage> = {
  '/welcome': 'welcome',
  '/projets': 'projects',
  '/portefeuille': 'portfolio',
};

/**
 * Resolve the path the effective user should land on: their profile's
 * configured welcome page when they can access it, then the organization's
 * landing page (ui_settings.landing), otherwise the first accessible page from
 * FALLBACK_CANDIDATES.
 */
export function useLandingPath(): string {
  const { can, profile } = usePermissions();
  const { moduleEnabled, settings } = useOrgModules();

  const orgLanding = settings?.landing ? ORG_LANDING_PAGE[settings.landing] : null;
  const preferred = [(profile?.landing_page ?? null) as LandingPage | null, orgLanding ?? null];
  for (const page of preferred) {
    if (!page) continue;
    const meta = LANDING_PAGE_META[page];
    // A page whose org module is off (e.g. projects before the Olisé preset) is skipped.
    const moduleOn = !meta || !isOrgModule(meta.resource) || moduleEnabled(meta.resource);
    if (meta && moduleOn && can(meta.resource, 'read')) return meta.path;
  }

  for (const candidate of FALLBACK_CANDIDATES) {
    if (can(candidate.resource, 'read')) return candidate.path;
  }
  return SAFE_FALLBACK;
}
